import { catchError, distinctUntilChanged, map, shareReplay, startWith, switchMap, tap } from 'rxjs/operators';
import { Injectable } from '@angular/core';
import { BehaviorSubject, combineLatest, filter, Observable, of, Subject } from 'rxjs';
import { GrowlerService } from '../../components/shared/growler/growler.service';
import { ApplicationHttpClient } from '../../components/shared/http/application-http-client';
import { Routes } from '../../config/routes';
import { JwtLegFiClaims } from '../auth/jwt-legfi-claims.model';
import { LegFiJwtService } from '../auth/legfi-jwt.service';
import { OrganizationFile } from '../../models/entities/organization-file';

export interface OwnerUploadFolder
{
    id: number;
    fileName: string;
    path: string;
}

export interface OwnerUpload
{
    id: number;
    fileName: string;
    fileSize: number;
    folderId: number;
    folderName: string;
    folderPath: string;
    createdAt: string;
    canDelete: boolean;
}

export interface DocumentSharesRequest
{
    documentIds: number[];
    unitIds?: number[] | null;
    membershipIds?: number[] | null;
    public?: boolean | null;
    includeContents?: boolean;
    removeUnitIds?: number[];
    removeMembershipIds?: number[];
    addUnitIds?: number[];
    addMembershipIds?: number[];
}

@Injectable({
    providedIn: 'root',
})
export class DocumentsService
{
    readonly orgId$ = new BehaviorSubject<number | null>(null);
    readonly clear$ = new Subject<void>();
    readonly loading$: BehaviorSubject<boolean> = new BehaviorSubject(true);

    /**
     * Flat documents list for the currently selected organization
     */
    readonly flatDocuments$: Observable<OrganizationFile[]> = combineLatest([
        this.orgId$.pipe(distinctUntilChanged()),
        this.clear$.pipe(startWith(undefined)),
    ]).pipe(
        map(([orgId]) => orgId),
        filter((orgId) => !!orgId),
        tap(() => this.loading$.next(true)),
        // catchError must live INSIDE switchMap: on the outer stream one transient failure ends the
        // shared stream for the whole session, and of(null) crashed the map below anyway.
        switchMap(orgId => this.getFlatDocumentsForOrganization(orgId).pipe(
            catchError((err) => {
                this._growler.fromErrorResponse(err);
                return of([]);
            }),
        )),
        tap(() => this.loading$.next(false)),
        map((response: Object[]) => response.map((file) => new OrganizationFile(file))),
        shareReplay({bufferSize: 1, refCount: false}),
    );

    constructor(
        private _http: ApplicationHttpClient,
        private _growler: GrowlerService,
    ) {
    }

    /**
     * Set the organization ID for which to retrieve flat documents list
     */
    setOrganizationId(orgId: number | null): void {
        this.orgId$.next(orgId);
    }

    /**
     * Clear the flat documents list cache
     */
    refresh(): void {
        this.clear$.next();
    }

    public getBulkDownload(organizationId: number, documentIds: number[]): Observable<Object> {
        const jwt: JwtLegFiClaims = LegFiJwtService.read();
        if (jwt === null) {
            return this._http.redirectAndThrow401Observable();
        }

        const url: string = Routes.MakeLegFiCoreUrl(Routes.LegFiCore.BulkOrgDocuments(organizationId || jwt.orgId));

        const options: Object = {
            responseType: 'blob',
        };

        return this._http.post(url, JSON.stringify({documents: documentIds}), options);
    }

    public downloadDocument(organizationId: number, document: OrganizationFile): Observable<Object> {
        const jwt: JwtLegFiClaims = LegFiJwtService.read();
        if (jwt === null) {
            return this._http.redirectAndThrow401Observable();
        }

        const url: string = Routes.MakeLegFiCoreUrl(Routes.LegFiCore.DownloadOrgDocument(organizationId || jwt.orgId, document.id));

        const options: Object = {
            responseType: 'blob',
        };

        return this._http.post(url, JSON.stringify({}), options);
    }

    public getFlatDocumentsForOrganization(organizationId: number, showHidden: boolean = false): Observable<Object> {
        const jwt: JwtLegFiClaims = LegFiJwtService.read();
        if (jwt === null) {
            return this._http.redirectAndThrow401Observable();
        }

        let url: string = Routes.MakeLegFiCoreUrl(
                Routes.LegFiCore.OrgDocuments(organizationId || jwt.orgId) + '/flat',
        );

        if (showHidden && jwt.superUser) {
            url += '?showHidden=1';
        }

        return this._http.get(url);
    }

    /**
     * Restore hidden files for an organization (superUser only).
     */
    public restoreHiddenFiles(organizationId: number, fileIds: number[]): Observable<Object> {
        const jwt: JwtLegFiClaims = LegFiJwtService.read();
        if (jwt === null) {
            return this._http.redirectAndThrow401Observable();
        }

        const url: string = Routes.MakeLegFiCoreUrl(
                Routes.LegFiCore.RestoreHiddenDocuments(organizationId || jwt.orgId),
        );

        return this._http.post(url, JSON.stringify({files: fileIds}));
    }

    public copyNewResourcesDocuments(organizationId: number): Observable<Object> {
        const jwt: JwtLegFiClaims = LegFiJwtService.read();
        if (jwt === null) {
            return this._http.redirectAndThrow401Observable();
        }

        const url: string = Routes.MakeLegFiCoreUrl(Routes.LegFiCore.CopyNewResourceDocuments(organizationId || jwt.orgId));

        return this._http.post(url, JSON.stringify({}));
    }

    /**
     * Get documents for a unit.
     * @param {number} organizationId
     * @param {number} unitId If provided, limits query to the specified document.
     * @returns {any}
     */
    public getDocumentsForUnit(organizationId: number, unitId: number): Observable<OrganizationFile[]> {
        const jwt: JwtLegFiClaims = LegFiJwtService.read();
        if (jwt === null) {
            return this._http.redirectAndThrow401Observable();
        }

        const url: string = Routes.MakeLegFiCoreUrl(
                Routes.LegFiCore.UnitDocuments(organizationId || jwt.orgId, unitId),
        );

        return this._http.get(url).pipe(map((response: Object[]) => {
            return response.map((file) => {
                return new OrganizationFile(file);
            });
        }));
    }

    /**
     * Rename a document.
     * @param {number} organizationId
     * @param {number} documentId If provided, limits query to the specified document.
     * @param requestBody
     * @returns {any}
     */
    public updateDocument(
            organizationId: number,
            documentId: number,
            requestBody: { fileName?: string, parentId?: number, action?: string, ownerUploads?: boolean },
    ): Observable<OrganizationFile> {
        const jwt: JwtLegFiClaims = LegFiJwtService.read();
        if (jwt === null) {
            return this._http.redirectAndThrow401Observable();
        }

        const url: string = Routes.MakeLegFiCoreUrl(
                Routes.LegFiCore.DocumentUpdate(organizationId || jwt.orgId, documentId),
        );

        return this._http.put(url, JSON.stringify(requestBody)).pipe(map((response: Object) => {
            return new OrganizationFile(response);
        }));
    }

    /** Saves the order of one folder's rows; `parentId` null is the top level. */
    public reorderDocuments(parentId: number | null, ids: number[]): Observable<unknown> {
        const jwt: JwtLegFiClaims = LegFiJwtService.read();
        if (jwt === null) {
            return this._http.redirectAndThrow401Observable();
        }

        return this._http.put(Routes.MakeLegFiCoreUrl(Routes.LegFiCore.DocumentOrder(jwt.orgId)), JSON.stringify({ parentId, ids }));
    }

    /**
     * Add ... folder to organization?
     * TODO - Refactor this, the function name and the actual route being built do not make sense semantically.
     *    Are we creating a folder or creating a document? If folders are documents, why not createDocument?
     * @param {number} organizationId
     * @param {any} requestBody
     * @returns {any}
     */
    public addFolderForOrganization(organizationId: number, requestBody: any): Observable<Object> {
        const jwt: JwtLegFiClaims = LegFiJwtService.read();
        if (jwt === null) {
            return this._http.redirectAndThrow401Observable();
        }

        const url: string = Routes.MakeLegFiCoreUrl(Routes.LegFiCore.OrgDocuments(organizationId || jwt.orgId));

        return this._http.post(url, JSON.stringify(requestBody));
    }

    /**
     * Delete a collection of files from the specified organization's documents.
     * @param {number} organizationId
     * @param {number[]} fileIds
     * @returns {any}
     */
    public deleteFiles(organizationId: number, fileIds: number[]): Observable<Object> {
        const jwt: JwtLegFiClaims = LegFiJwtService.read();
        if (jwt === null) {
            return this._http.redirectAndThrow401Observable();
        }

        const url: string = Routes.MakeLegFiCoreUrl(
                Routes.LegFiCore.OrgDocuments(organizationId || jwt.orgId) +
                '?files=' +
                encodeURIComponent(fileIds.join(',')),
        );

        return this._http.delete(url);
    }

    /**
     * Remove a file attachment for an email about to send (attachment has been saved, but email hasn't sent yet)
     * @param {number} organizationId
     * @param {number} fileId
     * @returns {any}
     */
    public removeEmailAttachment(organizationId: number, fileId: number): Observable<Object> {
        const jwt: JwtLegFiClaims = LegFiJwtService.read();
        if (jwt === null) {
            return this._http.redirectAndThrow401Observable();
        }

        const url: string = Routes.MakeLegFiCoreUrl(
                Routes.LegFiCore.DocumentDeleteEmailAttach(organizationId || jwt.orgId, fileId),
        );

        return this._http.delete(url);
    }

    /** Sets (or with remove*, trims) each document's sharing; a folder passes it inside unless includeContents is false. */
    public setShares(organizationId: number, body: DocumentSharesRequest): Observable<{ documentCount: number }> {
        const jwt: JwtLegFiClaims = LegFiJwtService.read();
        if (jwt === null) {
            return this._http.redirectAndThrow401Observable();
        }

        const url: string = Routes.MakeLegFiCoreUrl(Routes.LegFiCore.OrgDocumentShares(organizationId || jwt.orgId));

        return this._http.put<{ documentCount: number }>(url, JSON.stringify(body));
    }

    /** Adds one unit to just these documents, leaving every other share and the files inside folders alone. */
    public shareWithUnit(organizationId: number, documentIds: number[], unitId: number): Observable<{ documentCount: number }> {
        return this.setShares(organizationId, { documentIds, addUnitIds: [unitId], includeContents: false });
    }

    public getOrgDocumentDetails(organizationId: number, documentId: number): Observable<OrganizationFile> {
        const jwt: JwtLegFiClaims = LegFiJwtService.read();
        if (jwt === null) {
            return this._http.redirectAndThrow401Observable();
        }

        const url: string = Routes.MakeLegFiCoreUrl(
                Routes.LegFiCore.OrgDocumentDetails(organizationId || jwt.orgId, documentId),
        );

        return this._http.get(url).pipe(map((response: Object) => {
            return new OrganizationFile(response);
        }));
    }

    public getOrgUnitDocumentDetails(organizationId: number, documentId: number, unitId: number): Observable<OrganizationFile> {
        const jwt: JwtLegFiClaims = LegFiJwtService.read();
        if (jwt === null) {
            return this._http.redirectAndThrow401Observable();
        }
        const url: string = Routes.MakeLegFiCoreUrl(
                Routes.LegFiCore.OrgUnitDocumentDetails(organizationId || jwt.orgId, documentId, unitId),
        );

        return this._http.get(url).pipe(map((response: Object) => {
            return new OrganizationFile(response);
        }));
    }

    public getOwnerUploadFolders(organizationId: number, unitId: number): Observable<OwnerUploadFolder[]> {
        const jwt: JwtLegFiClaims = LegFiJwtService.read();
        if (jwt === null) {
            return this._http.redirectAndThrow401Observable();
        }
        const url: string = Routes.MakeLegFiCoreUrl(
                Routes.LegFiCore.UnitDocumentUploadFolders(organizationId || jwt.orgId, unitId),
        );

        return this._http.get<OwnerUploadFolder[]>(url);
    }

    public getMyOwnerUploads(organizationId: number, unitId: number): Observable<OwnerUpload[]> {
        const jwt: JwtLegFiClaims = LegFiJwtService.read();
        if (jwt === null) {
            return this._http.redirectAndThrow401Observable();
        }
        const url: string = Routes.MakeLegFiCoreUrl(
                Routes.LegFiCore.UnitDocumentMyUploads(organizationId || jwt.orgId, unitId),
        );

        return this._http.get<OwnerUpload[]>(url);
    }

    public deleteMyOwnerUpload(organizationId: number, unitId: number, documentId: number): Observable<Object> {
        const jwt: JwtLegFiClaims = LegFiJwtService.read();
        if (jwt === null) {
            return this._http.redirectAndThrow401Observable();
        }
        const url: string = Routes.MakeLegFiCoreUrl(
                Routes.LegFiCore.UnitDocumentMyUpload(organizationId || jwt.orgId, unitId, documentId),
        );

        return this._http.delete(url);
    }
}
